import { computed, type Ref } from "vue";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
} from "@/common/localStorageUtil";
import { CHAIN_REGISTER_FILE_NAME, REQUIRED_FIELD_LABEL_KEYS } from "../constants";

/** チェーン登録 初期化処理に必要な依存をまとめた型。 */
interface UseChainRegisterInitDeps {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  overlay: Ref<boolean>;
  wordList: Ref<Record<string, string>>;
  chainListItems: Ref<
    ({ __typename?: "Chain"; chain_code: string; name: string } | null | undefined)[] | null
  >;
  getWordListData: (chainCode: string, screenId: string, customerCode: string) => Promise<Record<string, string>>;
  getChainListData: (customerCode: string) => Promise<any>;
}

/**
 * チェーン登録 初期化処理を扱う composable。
 * 画面初期ロード（onMounted 処理の委譲先）を管理する。
 */
export const useChainRegisterInit = (deps: UseChainRegisterInitDeps) => {
  const {
    errdialog,
    errMessage,
    overlay,
    wordList,
    chainListItems,
    getWordListData,
    getChainListData,
  } = deps;

  // wordListが準備できているかをチェック（必要なラベルキーが全て存在するか）
  const isWordListReady = computed(() => {
    if (Object.keys(wordList.value).length === 0) return false;
    return REQUIRED_FIELD_LABEL_KEYS.every((key) => key in wordList.value);
  });

  /**
   * 初期ロード実行（onMounted処理の委譲先）
   */
  async function runInitialLoad(): Promise<void> {
    try {
      overlay.value = true;

      // 文言データを取得する
      wordList.value = await getWordListData(
        localStorageGetChainCode(),
        CHAIN_REGISTER_FILE_NAME,
        localStorageGetCustomerCode()
      );

      // チェーンリストを取得する(名前重複チェックのため)
      chainListItems.value = await getChainListData(localStorageGetCustomerCode());
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;

      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
      }

      return;
    } finally {
      overlay.value = false;
    }
  }

  return {
    isWordListReady,
    runInitialLoad,
  };
};
